/*
  PaoPao wishlist — Customer Account API sync (new customer accounts).

  Treats the theme JS as a PUBLIC web client of the Customer Account API
  (OAuth 2.0 + PKCE) and reads/writes the customer JSON metafield
  `paopao.wishlist`. Exposes window.WishlistAccount; assets/wishlist.js layers
  the actual sync on top of this.

  Silent-first: this module NEVER triggers a full-page redirect on its own.
  ensureToken() resolves a token when possible (memory → stored refresh token →
  best-effort prompt=none in a hidden iframe) and resolves null otherwise, so a
  buyer who isn't signed in on the storefront just stays guest-local. A visible
  login is opt-in via WishlistAccount.signIn().

  Config comes from window.PaoPaoWishlistConfig.clientId (set in layout/theme).
  Requires one-time Shopify setup: a Customer Account API public client (client
  id + this origin registered as a JS origin and `<origin>/pages/wishlist-auth`
  as a redirect URI), protected-customer-data approval, and a `paopao.wishlist`
  customer metafield definition with Customer Account API read/write access.
*/
(function () {
  'use strict';

  var CFG = window.PaoPaoWishlistConfig || {};
  var CLIENT_ID = CFG.clientId || '';
  var ACCOUNT_URL = CFG.accountUrl || '/account';
  var CALLBACK_PATH = '/pages/wishlist-auth';
  var SCOPES = 'openid email customer-account-api:full';
  var NS = 'paopao';
  var KEY = 'wishlist';

  // localStorage keys (same-origin sharing between the page and the auth
  // callback — including the silent iframe — is required for the PKCE handoff).
  var K = {
    verifier: 'paopao:ca:verifier',
    state: 'paopao:ca:state',
    ret: 'paopao:ca:return',
    access: 'paopao:ca:access',
    refresh: 'paopao:ca:refresh',
    exp: 'paopao:ca:exp',
    pending: 'paopao:ca:pending',      // set when the buyer left to log in via /account
    justLinked: 'paopao:ca:justlinked' // set when a guest initiates login → fold local items once
  };

  // sessionStorage keys: cache discovery, and guard the auto-bootstrap redirect
  // so it fires at most once per session (a redirect navigates away + back).
  var SK = { disco: 'paopao:ca:disco', tried: 'paopao:ca:tried' };

  var mem = { access: null, exp: 0 };
  var discoveryPromise = null;
  var ensuring = null;
  var refreshing = null;

  function origin() { return window.location.origin; }
  function redirectUri() { return origin() + CALLBACK_PATH; }
  function ls(get, key, val) {
    try { return get ? localStorage.getItem(key) : localStorage.setItem(key, val); }
    catch (e) { return null; }
  }
  function lsDel(key) { try { localStorage.removeItem(key); } catch (e) {} }
  function ss(get, key, val) {
    try { return get ? sessionStorage.getItem(key) : sessionStorage.setItem(key, val); }
    catch (e) { return null; }
  }

  /* ------------------------------------------------------- PKCE / randomness */
  function b64url(buf) {
    var bytes = new Uint8Array(buf), s = '';
    for (var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function randomString(len) {
    var arr = new Uint8Array(len);
    crypto.getRandomValues(arr);
    return b64url(arr).slice(0, len);
  }
  function challengeFor(verifier) {
    return crypto.subtle
      .digest('SHA-256', new TextEncoder().encode(verifier))
      .then(b64url);
  }

  /* --------------------------------------------------------------- discovery */
  function discover() {
    if (discoveryPromise) return discoveryPromise;
    var cached = ss(true, SK.disco);
    if (cached) {
      try { discoveryPromise = Promise.resolve(JSON.parse(cached)); return discoveryPromise; }
      catch (e) {}
    }
    discoveryPromise = Promise.all([
      fetch('/.well-known/openid-configuration').then(function (r) { return r.json(); }),
      fetch('/.well-known/customer-account-api').then(function (r) { return r.json(); })
    ]).then(function (res) {
      var d = { openid: res[0], ca: res[1] };
      ss(false, SK.disco, JSON.stringify(d));
      return d;
    }).catch(function (e) {
      discoveryPromise = null; // allow retry
      throw e;
    });
    return discoveryPromise;
  }

  /* ------------------------------------------------------------------ tokens */
  function storeTokens(t) {
    // 30s skew so we refresh slightly before the real expiry.
    var exp = t.expires_in ? Date.now() + (t.expires_in * 1000 - 30000) : 0;
    mem.access = t.access_token;
    mem.exp = exp;
    ls(false, K.access, t.access_token || '');
    ls(false, K.exp, String(exp));
    if (t.refresh_token) ls(false, K.refresh, t.refresh_token);
  }
  function loadTokens() {
    mem.access = ls(true, K.access) || null;
    mem.exp = parseInt(ls(true, K.exp) || '0', 10) || 0;
  }
  function clearTokens() {
    mem.access = null; mem.exp = 0;
    [K.access, K.refresh, K.exp].forEach(lsDel);
  }
  // Drop only the access token (keep the refresh token) — used when a call 401s
  // so we re-derive a fresh access token from the still-valid refresh token.
  function invalidateAccess() {
    mem.access = null; mem.exp = 0;
    lsDel(K.access); lsDel(K.exp);
  }
  function haveValidAccess() { return !!mem.access && Date.now() < mem.exp; }

  function refreshWithToken() {
    var rt = ls(true, K.refresh);
    if (!rt) return Promise.reject(new Error('no_refresh'));
    return discover().then(function (d) {
      var body = new URLSearchParams();
      body.append('grant_type', 'refresh_token');
      body.append('client_id', CLIENT_ID);
      body.append('refresh_token', rt);
      return fetch(d.openid.token_endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: body
      });
    }).then(function (r) {
      // 400/401 = the refresh token was rejected (revoked / logged out). Clear
      // everything so the buyer is treated as a guest again. Other failures are
      // transient — keep the tokens and let the next load retry.
      if (r.status === 400 || r.status === 401) {
        clearTokens();
        throw new Error('refresh_revoked');
      }
      if (!r.ok) throw new Error('refresh_failed');
      return r.json();
    }).then(function (t) { storeTokens(t); return mem.access; });
  }

  // Coalesced refresh — Customer Account API refresh tokens are single-use, so
  // concurrent callers must share one in-flight refresh, not each spend the token.
  function doRefresh() {
    if (refreshing) return refreshing;
    refreshing = refreshWithToken()
      .then(function (tok) { refreshing = null; return tok; })
      .catch(function (e) { refreshing = null; throw e; });
    return refreshing;
  }

  function exchangeCode(code, verifier) {
    return discover().then(function (d) {
      var body = new URLSearchParams();
      body.append('grant_type', 'authorization_code');
      body.append('client_id', CLIENT_ID);
      body.append('redirect_uri', redirectUri());
      body.append('code', code);
      body.append('code_verifier', verifier);
      return fetch(d.openid.token_endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: body
      });
    }).then(function (r) {
      if (!r.ok) throw new Error('token_exchange_failed');
      return r.json();
    });
  }

  /* --------------------------------------------------- authorization request */
  function buildAuthUrl(opts) {
    return discover().then(function (d) {
      var verifier = randomString(64);
      var state = randomString(32);
      return challengeFor(verifier).then(function (challenge) {
        ls(false, K.verifier, verifier);
        ls(false, K.state, state);
        var u = new URL(d.openid.authorization_endpoint);
        u.searchParams.append('scope', SCOPES);
        u.searchParams.append('client_id', CLIENT_ID);
        u.searchParams.append('response_type', 'code');
        u.searchParams.append('redirect_uri', redirectUri());
        u.searchParams.append('state', state);
        u.searchParams.append('nonce', randomString(24));
        u.searchParams.append('code_challenge', challenge);
        u.searchParams.append('code_challenge_method', 'S256');
        if (opts && opts.prompt) u.searchParams.append('prompt', opts.prompt);
        return u.toString();
      });
    });
  }

  // Automatic bootstrap of the FIRST token of a session, WITHOUT a button.
  // A hidden iframe can't do this — inside a frame the Shopify auth cookie is a
  // third-party cookie and browsers block it — so we use a TOP-LEVEL prompt=none
  // redirect. For a buyer who already has a Shopify session this bounces through
  // with no login screen and returns synced; a stored refresh token then keeps
  // every later visit fully silent. Guarded to run at most once per session, and
  // callers should only invoke it for a buyer believed to be logged in, so
  // guests are never redirected. If there's no session, Shopify returns
  // login_required to the callback, which just lands the buyer back (guest).
  function autoBootstrap() {
    if (!CLIENT_ID || haveValidAccess()) return;
    if (ss(true, SK.tried)) return;            // already attempted this session
    ss(false, SK.tried, '1');
    ls(false, K.ret, window.location.pathname + window.location.search);
    buildAuthUrl({ prompt: 'none' })
      .then(function (url) { window.location.href = url; })
      .catch(function () {});
  }

  // Resolves an access token or null WITHOUT ever navigating: in-memory token,
  // else a background refresh-token exchange, else null. Never shows UI or
  // redirects — safe to call before any read/write. Getting the first token is
  // autoBootstrap()'s job.
  function ensureToken() {
    if (!CLIENT_ID) return Promise.resolve(null);
    if (haveValidAccess()) return Promise.resolve(mem.access);
    if (ensuring) return ensuring;
    ensuring = doRefresh()
      .catch(function () { return null; })
      .then(function (tok) { ensuring = null; return tok; });
    return ensuring;
  }

  /* ----------------------------------------------------------------- GraphQL */
  // The Customer Account API expects the RAW token (prefixed `shcat_`) in the
  // Authorization header — NOT "Bearer <token>" (that returns
  // "Invalid token, missing prefix shcat_").
  function gqlOnce(tok, query, variables, opts) {
    return discover().then(function (d) {
      var init = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': tok },
        body: JSON.stringify({ query: query, variables: variables || {} }),
        cache: 'no-store'   // always read fresh — avoid a stale cached metafield after a cross-device write
      };
      if (opts && opts.keepalive) init.keepalive = true;
      return fetch(d.ca.graphql_api, init);
    });
  }
  function gql(query, variables, opts) {
    return ensureToken().then(function (tok) {
      if (!tok) throw new Error('not_authed');
      return gqlOnce(tok, query, variables, opts).then(function (r) {
        if (r.status !== 401) return r;
        // Token dead/revoked — drop it, get a fresh one from the refresh token,
        // and retry once. If the refresh token is dead too, clear everything so
        // the next load re-bootstraps (autoBootstrap / the toast).
        invalidateAccess();
        return doRefresh().catch(function () { return null; }).then(function (tok2) {
          if (!tok2) { clearTokens(); throw new Error('unauthorized'); }
          return gqlOnce(tok2, query, variables, opts);
        });
      });
    }).then(function (r) { return r.json(); });
  }

  /* -------------------------------------------------------------- favorites  */
  function getFavorites() {
    var q = 'query { customer { id metafield(namespace: "' + NS + '", key: "' + KEY + '") { value } } }';
    return gql(q).then(function (res) {
      var c = res && res.data && res.data.customer;
      if (!c) return { id: null, items: [] };
      var items = [];
      if (c.metafield && c.metafield.value) {
        try { items = JSON.parse(c.metafield.value) || []; } catch (e) {}
      }
      return { id: c.id, items: items };
    });
  }
  function saveFavorites(customerId, list, opts) {
    if (!customerId) return Promise.reject(new Error('no_customer_id'));
    var m =
      'mutation($ownerId: ID!, $value: String!) {' +
      '  metafieldsSet(metafields: [{ ownerId: $ownerId, namespace: "' + NS + '", key: "' + KEY + '", type: "json", value: $value }]) {' +
      '    userErrors { field message }' +
      '  }' +
      '}';
    return gql(m, { ownerId: customerId, value: JSON.stringify(list) }, opts);
  }

  /* --------------------------------------- generic customer metafield access */
  // Self-contained JSON metafield read/write in the `paopao` namespace, for
  // other features (e.g. saved custom pieces). Handles auth + the customer id
  // internally. getMetafield resolves the PARSED value ([] when empty) or null
  // when not signed in; setMetafield resolves null when not signed in.
  var cachedCustomerId = null;
  function getCustomerId() {
    if (cachedCustomerId) return Promise.resolve(cachedCustomerId);
    return gql('query { customer { id } }').then(function (res) {
      cachedCustomerId = res && res.data && res.data.customer && res.data.customer.id;
      return cachedCustomerId;
    });
  }
  function accountGetMetafield(key) {
    var q = 'query { customer { id metafield(namespace: "' + NS + '", key: "' + key + '") { value } } }';
    return gql(q).then(function (res) {
      var c = res && res.data && res.data.customer;
      if (!c) return null;
      cachedCustomerId = c.id || cachedCustomerId;
      if (!c.metafield || !c.metafield.value) return [];
      try { return JSON.parse(c.metafield.value); } catch (e) { return null; }
    }).catch(function () { return null; });   // not_authed / error → null
  }
  function accountSetMetafield(key, value) {
    return getCustomerId().then(function (cid) {
      if (!cid) return null;
      return setMetafieldRaw(cid, key, value);
    }).catch(function () { return null; });
  }
  function setMetafieldRaw(customerId, key, value) {
    var m =
      'mutation($ownerId: ID!, $value: String!) {' +
      '  metafieldsSet(metafields: [{ ownerId: $ownerId, namespace: "' + NS + '", key: "' + key + '", type: "json", value: $value }]) {' +
      '    userErrors { field message }' +
      '  }' +
      '}';
    var v = typeof value === 'string' ? value : JSON.stringify(value);
    return gql(m, { ownerId: customerId, value: v });
  }

  /* -------------------------------------------------- interactive sign in/out */
  function signIn() {
    if (!CLIENT_ID) return;
    try { sessionStorage.removeItem(SK.tried); } catch (e) {}
    ls(false, K.ret, window.location.pathname + window.location.search);
    buildAuthUrl({}).then(function (url) { window.location.href = url; });
  }

  // Send the buyer to the store's login page (/account). We flag the intent so
  // that when they come back signed in, the next load silently bootstraps a token
  // (prompt=none) and syncs — see the pending check in init(). This is the action
  // wired to the sign-in toast.
  function login() {
    ls(false, K.pending, '1');
    // A per-login token: each feature (wishlist, custom pieces) folds its guest
    // items once for THIS login, then compares the token to know not to re-fold.
    ls(false, K.justLinked, 'link_' + Date.now());
    ls(false, K.ret, window.location.pathname + window.location.search);
    try { sessionStorage.removeItem(SK.tried); } catch (e) {}
    window.location.href = ACCOUNT_URL;
  }
  function signOut() { clearTokens(); }

  /* -------------------------------------------------------- callback handler */
  function redirectBack() {
    var ret = ls(true, K.ret) || '/';
    lsDel(K.ret);
    window.location.replace(ret);
  }
  function handleCallback() {
    var params = new URLSearchParams(window.location.search);
    var code = params.get('code');
    var state = params.get('state');
    var error = params.get('error');

    // Silent (iframe) flow: hand the result to the opener, which owns the
    // verifier and completes the exchange.
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage(
          { type: 'paopao-wishlist-auth', code: code, state: state, error: error },
          origin()
        );
      } catch (e) {}
      return;
    }

    // Top-level (interactive) flow: exchange here, then return the buyer back.
    if (error || !code || state !== ls(true, K.state)) { redirectBack(); return; }
    exchangeCode(code, ls(true, K.verifier))
      .then(function (t) { storeTokens(t); })
      .catch(function () {})
      .then(redirectBack);
  }

  /* -------------------------------------------------------------------- init */
  loadTokens();

  if (window.location.pathname.replace(/\/$/, '') === CALLBACK_PATH) {
    handleCallback();
  } else if (ls(true, K.pending) && CLIENT_ID && !haveValidAccess()) {
    // The buyer went to log in via /account and is now back. Consume the flag and
    // silently bootstrap a token (prompt=none) so their favorites/pieces sync.
    lsDel(K.pending);
    autoBootstrap();
  }

  var hasStoredSession = function () { return !!ls(true, K.refresh); };

  window.WishlistAccount = {
    isConfigured: function () { return !!CLIENT_ID; },
    isAuthed: haveValidAccess,
    // True when this device has ever linked an account (a refresh token exists),
    // even if the access token has lapsed — used to avoid re-nudging linked buyers.
    hasStoredSession: hasStoredSession,
    ensureToken: ensureToken,
    autoBootstrap: autoBootstrap,
    getFavorites: getFavorites,
    saveFavorites: saveFavorites,
    login: login,
    signIn: signIn,
    signOut: signOut
  };

  // Generic account API shared by other features (e.g. saved custom pieces).
  window.PaoPaoAccount = {
    isConfigured: function () { return !!CLIENT_ID; },
    isAuthed: haveValidAccess,
    hasStoredSession: hasStoredSession,
    ensureToken: ensureToken,
    autoBootstrap: autoBootstrap,
    login: login,                        // → /account, then auto-sync on return
    signIn: signIn,
    signOut: signOut,
    getMetafield: accountGetMetafield,   // (key) -> parsed value | [] | null
    setMetafield: accountSetMetafield    // (key, value) -> result | null
  };
})();
